import React, { useState, useEffect, useCallback, Fragment } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ShareQRCode from "../common/shareQRCode/shareQRCode";
import { ShareModalProps } from "./ShareComponent.types";
import { sendSMS } from "../../utils/share";
import StashrunLogoBeta from "./../../assets/images/Stashrun-beta-logo-grey.png";
import useShareModal from "./hooks/useShareModal";
import VerifiedIcon from "./../../assets/images/VerifiedIcon.svg";
import { convertHttpToHttps, generateCloudinaryImageUrl, getLocalStorage, splitName } from "../../utils/common-function";
import { RxCross2 } from "react-icons/rx";
import mixpanel from "../../services/mixpanelService";
import Loader from "../common/loader/loader";
import { SHARE_STASHLINK } from "../../queries";
import { useMutation } from "@apollo/client";
import { Stashlink } from "../../types";
import SharePicker from "./SharePicker";
import clsx from "clsx";
import CopyButton from "./CopyButton";
import { isAmazonCampaign, isNativeWebview } from "../../common/constants";

const ShareComponent: React.FC<ShareModalProps> = (props: ShareModalProps) => {
  const { imageRef, imgSrc } = useShareModal(props);
  const [showQr, setShowQr] = useState<boolean>(false);
  const [longPress, setLongPress] = useState<boolean>(false);
  const [isPickerOpen, setIsPickerOpen] = useState<boolean>(true);
  const [pressTimer, setPressTimer] = useState<NodeJS.Timeout | null>(null);
  const [shareStashlink] = useMutation(SHARE_STASHLINK);
  const stashlink = getLocalStorage("stashlink") as Stashlink;
  const { firstName, lastName } = splitName(props.stashUser?.fullName || "");
  const { firstName: colinkUserFirstName, lastName: colinkUserLastName } = splitName(props.coLinkUser?.fullName || "");
  const isAmazon = isAmazonCampaign(stashlink?.id);

  const handleLongPress = useCallback(() => {
    shareStashlink({
      variables: {
        stashlinkId: stashlink?.id
      }
    });
    mixpanel.track("long-press-detected", {
      charityName: props.charityName,
      shareUrl: props.shareUrl
    });
  }, []);

  const startPressTimer = useCallback(() => {
    setPressTimer(
      setTimeout(() => {
        setLongPress(true);
      }, 800) // 800ms for long press
    );
  }, []);

  const clearPressTimer = useCallback(() => {
    if (pressTimer) {
      clearTimeout(pressTimer);
      setPressTimer(null);
    }
    setLongPress(false);
  }, [pressTimer]);

  useEffect(() => {
    if (longPress) {
      handleLongPress();
    }
  }, [longPress, handleLongPress]);

  const shareToInstagram = useCallback(async () => {
    if (navigator.share) {
      // Convert base64 to blob
      const response = await fetch(imgSrc);
      const blob = await response.blob();

      // Create file from blob
      const file = new File([blob], "canvas-image.png", { type: "image/png" });

      navigator
        .share({
          // title: stashlink?.campaign.title ?? "Stashrun",
          // text: message,
          // url: props.shareUrl,
          files: [file]
        })
        .catch(console.error);
    }
  }, [props.shareUrl, props.charityName, imgSrc]);

  useEffect(() => {
    if (props.isOpen) {
      setIsPickerOpen(true);
      document.body.classList.add("prevent-scroll");
    } else {
      document.body.classList.remove("prevent-scroll");
    }
    return () => {
      document.body.classList.remove("prevent-scroll");
    };
  }, [props.isOpen]);

  return (
    <div
      className={`fixed inset-0 z-50 flex flex-col justify-start bg-soft-black text-white rounded-t-2xl transition-transform duration-300 ease-out ${
        props.isOpen ? "translate-y-0" : "translate-y-full"
      }`}>
      <div
        className={clsx([
          "absolute z-20 rounded-t-2xl h-dvh w-screen bg-soft-black overflow-y-scroll pb-12",
          isPickerOpen ? "block" : "hidden"
        ])}>
        <SharePicker
          onClosePicker={() => {
            setIsPickerOpen(false);
          }}
          onClose={props.onClose}
          stashlink={stashlink}
          shareUrl={props.shareUrl || ""}
          stashUser={props.stashUser}
          coLinkUser={props.coLinkUser}
          sendSMS={() => sendSMS(props.shareUrl || "", props.charityName || "", isAmazon)}
        />
      </div>
      <div className={clsx([isPickerOpen ? "opacity-0" : "opacity-100"])}>
        <header className="text-center p-4 pb-0 flex flex-col">
          <div className="flex flex-col justify-center items-center">
            <p className="text-xl font-bold font-primary text-base-white leading-normal">Share to raise more</p>
          </div>
          <button onClick={props.onClose} className="absolute right-4 text-grey text-lg hover:text-gray-100 font-semibold z-10">
            <RxCross2 />
          </button>
        </header>
        <div className="flex flex-col h-full overflow-y-scroll">
          <main className="h-full p-12 pt-4 pb-0 relative" style={{ height: isNativeWebview() ? "45vh" : "55vh" }}>
            {imgSrc ? (
              <div className="overflow-hidden h-full relative mx-auto">
                <div
                  className="relative flex items-end w-full select-none h-full justify-center"
                  onMouseDown={startPressTimer}
                  onMouseUp={clearPressTimer}
                  onMouseLeave={clearPressTimer}
                  onTouchStart={startPressTimer}
                  onTouchEnd={clearPressTimer}>
                  <img
                    src={imgSrc}
                    alt="User Stash Link"
                    className="border-2 border-black xxsh:border-b-0 rounded-3xl xxsh:rounded-b-none h-full object-contain"
                  />
                </div>
              </div>
            ) : (
              <div className="h-screen flex flex-col items-center justify-start p-12 top-20 mt-24">
                <Loader />
              </div>
            )}
          </main>
          <footer className="w-full bg-primary-l1 border-modal-top-bg z-10">
            <div className="bg-soft-black w-full py-4 xxsh:py-2 px-8">
              <div className="mb-3 text-center text-neutral-50 text-sm font-semibold font-['Inter'] leading-[16.80px]">
                Remember to add the stashlink to
                <br />
                your Instagram story and bio
              </div>
              <div className="h-12 pl-4 pr-1 w-full py-2 bg-black/10 rounded-xl border-l border-r border-t border-b-2 border-[#90a8c1]/30 justify-center items-center gap-2 inline-flex">
                <div className="grow shrink basis-0 flex-col justify-center items-center inline-flex">
                  <div className="self-stretch text-[#b0c1d3] text-[10px] font-normal font-['Inter'] leading-3">Your unique link</div>
                  <div className="self-stretch text-neutral-50 text-sm font-normal font-['Inter'] leading-[18.20px]">
                    {props.shareUrl?.slice(0, 30)}...
                  </div>
                </div>
                <CopyButton copyOnStart={!isPickerOpen} stashlink={stashlink} shareUrl={props.shareUrl || ""} />
              </div>
              <div
                onClick={shareToInstagram}
                className="my-6 text-center w-full h-12 px-4 py-3 font-primary text-base leading-tight tracking-wider font-wght-740 gap-2 rounded-lg text-black border-2 border-black select-none bg-primary-l1 shadow-[0px_4px_0px_#6C801F]">
                Continue to Instagram
              </div>
            </div>
          </footer>
        </div>
        <div
          className="absolute top-[-2000px] left-[-1200px]"
          onMouseDown={startPressTimer}
          onMouseUp={clearPressTimer}
          onMouseLeave={clearPressTimer}
          onTouchStart={startPressTimer}
          onTouchEnd={clearPressTimer}>
          <div ref={imageRef}>
            {isAmazon ? (
              <img src="/campaign/amazon_campaign_ig.png" alt="amazon_camapign" height={375} width={696} />
            ) : (
              <Fragment>
                <div
                  className="share-modal-bg px-6 pt-12 pb-32 w-[270px] select-none"
                  style={{
                    userSelect: "none",
                    MozUserSelect: "none",
                    WebkitUserSelect: "none",
                    msUserSelect: "none"
                  }}>
                  <div className="bg-soft-black rounded-2xl overflow-hidden border-r-2.44 border-b-2.44 border-primary-l1">
                    <div className="bg-black/30">
                      <div className="bg-soft-black rounded-[12px] p-2 pb-1">
                        <div className="relative overflow-hidden mb-0.5">
                          {props.coLinkUser ? (
                            <div className="flex w-full aspect-video">
                              <div className="rounded-l-[8px] w-1/2 h-full border-r-2 border-soft-black flex items-center justify-center">
                                <img
                                  src={generateCloudinaryImageUrl(convertHttpToHttps(props.stashUser.profilePhoto) || "", "320", "360")}
                                  alt="Stash User"
                                  className="w-full h-full object-cover rounded-l-[8px]"
                                />
                              </div>
                              <div className="rounded-r-[8px] w-1/2 h-full border-l-2 border-soft-black flex items-center justify-center">
                                <img
                                  src={generateCloudinaryImageUrl(convertHttpToHttps(props.coLinkUser.profilePhoto) || "", "320", "360")}
                                  alt="Co-link User"
                                  className="w-full h-full object-cover rounded-r-[8px]"
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="rounded-[8px] w-full aspect-video flex items-center justify-center">
                              <img
                                src={generateCloudinaryImageUrl(convertHttpToHttps(props.stashUser.profilePhoto) || "", "960", "540")}
                                alt="Stash User"
                                className="w-full h-full object-cover rounded-[8px] c-object-fit"
                              />
                            </div>
                          )}
                          <img
                            src={
                              props.coLinkUser
                                ? generateCloudinaryImageUrl(convertHttpToHttps(props.charityLogo) || "", "89", "89")
                                : generateCloudinaryImageUrl(convertHttpToHttps(props.charityLogo) || "", "120", "120")
                            }
                            alt="Logo"
                            className={`absolute -bottom-1 ${
                              props.coLinkUser ? "left-2/4 -translate-x-2/4" : "-left-1"
                            } w-11 h-11 rounded-full border-4 border-soft-black`}
                            crossOrigin="anonymous"
                          />
                        </div>
                        <div className="text-center relative -top-2">
                          {props.coLinkUser ? (
                            <>
                              <p className="select-none text-base font-extrabold font-secondary text-primary-l1 inline-block m-0 leading-tight">
                                {firstName}{" "}
                                <span className="inline-block">
                                  {lastName}
                                  {props.stashUser?.isPublicProfile && (
                                    <img
                                      src={VerifiedIcon}
                                      alt={VerifiedIcon}
                                      className="ml-0.5 w-8.98 h-8.98 inline-block align-middle relative top-1"
                                    />
                                  )}
                                </span>
                              </p>
                              <p className="select-none text-primary-l1 text-xs font-primary font-extrabold m-0 -mt-1.5 -mb-1.5">x</p>
                              <p className="select-none text-base font-extrabold font-secondary text-primary-l1 inline-block m-0 leading-tight">
                                {colinkUserFirstName}{" "}
                                <span className="inline-block">
                                  {colinkUserLastName}
                                  {props.coLinkUser?.isPublicProfile && (
                                    <img
                                      src={VerifiedIcon}
                                      alt={VerifiedIcon}
                                      className="ml-0.5 w-8.98 h-8.98 inline-block align-middle relative top-1"
                                    />
                                  )}
                                </span>
                              </p>
                            </>
                          ) : (
                            <p className="select-none text-base font-extrabold font-secondary text-primary-l1 inline-block">
                              {firstName}{" "}
                              <span className="inline-block">
                                {lastName}
                                {props.stashUser?.isPublicProfile && (
                                  <img
                                    src={VerifiedIcon}
                                    alt={VerifiedIcon}
                                    className="ml-0.5 w-8.98 h-8.98 inline-block align-middle relative top-1"
                                  />
                                )}
                              </span>
                            </p>
                          )}
                          <p className="select-none text-10.48 text-base-white font-secondary font-normal">is stashing for</p>
                          <p className="select-none text-sm text-base-white font-bold font-secondary">{props.charityName}</p>
                        </div>
                      </div>

                      <div className="px-2 py-3 flex justify-center items-center flex-col">
                        <p className="text-xs text-center text-primary-l1 mb-4">❤ Free to play ❤ Free to plant ❤</p>
                        <div>
                          <img src={StashrunLogoBeta} alt={StashrunLogoBeta} className="h-2" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute left-2/4 -translate-x-2/4 bottom-16 w-[calc(100vh+10px)] bg-primary-l1 text-black text-center text-sm leading-none font-bold tracking-wide font-primary py-2 -rotate-3">
                  <span className="select-none relative -top-2">👇 Play to fund 👇 Play to fund 👇 Play to fund 👇</span>
                </div>
              </Fragment>
            )}
          </div>
        </div>

        {showQr && (
          <>
            {props.coLinkUser ? (
              <ShareQRCode
                stashUser={props.stashUser}
                coLinkUser={props.coLinkUser}
                charityName={props.charityName}
                charityLogo={props.charityLogo}
                shareUrl={props.shareUrl}
                isOpen={showQr}
                onClose={() => setShowQr(false)}
              />
            ) : (
              <ShareQRCode
                stashUser={props.stashUser}
                charityName={props.charityName}
                charityLogo={props.charityLogo}
                shareUrl={props.shareUrl}
                isOpen={showQr}
                onClose={() => setShowQr(false)}
              />
            )}
          </>
        )}
      </div>
      <ToastContainer
        className="fixed left-1/2 transform -translate-x-1/2 z-50 w-[10.5rem] h-[10.5rem] top-[17.65625rem] -mt-16"
        toastClassName={() =>
          "w-full h-full bg-copied-toaster-bg/30 text-white flex items-center justify-center rounded-[20px] backdrop-filter-blur"
        }
      />
    </div>
  );
};
export default ShareComponent;
